import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { assert, PoFormUtils } from 'ats-ui-lib';
import {
  ComplementaryFormAnswers,
  ComplementaryFormQuestionAnswers,
} from '../../models/job-opportunity-application.model';
import { FactoryFormMultipleChoiceQuestion } from './factory-form-answer-multiple-choice-question';
import { FactoryFormAnswerOpenQuestion } from './factory-form-answer-open-question';
import {
  ComplementaryForm,
  JobApplicationComplementaryFormQuestionTypeEnum,
} from './job-application-complementary-form.model';

@Injectable({
  providedIn: 'root',
})
export class FormQuestionOrchestrationService {
  public answersFormGroup?: UntypedFormGroup;

  public get existFormGroup(): boolean {
    return !!this.answersFormGroup;
  }

  public get isValid(): boolean {
    return !!this.answersFormGroup && this.answersFormGroup.valid;
  }

  public buildComplementaryForm(
    fb: UntypedFormBuilder,
    form: UntypedFormGroup,
    model: ComplementaryForm,
  ): UntypedFormGroup {
    assert(form);

    this.answersFormGroup = undefined;

    if (form.controls.answers === undefined) form.addControl('answers', fb.array([]));

    const answers = form.controls.answers as UntypedFormArray;

    const { questions } = model;

    for (let i = 0; i < questions.length; i += 1) {
      const question = questions[i];
      if (
        question &&
        question.type === JobApplicationComplementaryFormQuestionTypeEnum.OpenQuestion
      ) {
        const fg = FactoryFormAnswerOpenQuestion.create(
          fb,
          question.id,
          question.label,
          question.required,
          question.enableAttachment,
          question.requiredAttachment,
          i + 1,
        );

        answers.push(fg);
      } else {
        const fg = FactoryFormMultipleChoiceQuestion.create(
          fb,
          question.id,
          question.answers,
          question.label,
          question.required,
          i + 1,
        );

        answers.push(fg);
      }
    }

    this.answersFormGroup = form;
    return form;
  }

  public mergeQuestionsWithAnswers(): ComplementaryFormQuestionAnswers[] {
    const complementaryFormAnswers = this.getAnswers();
    const model = Array<ComplementaryFormQuestionAnswers>();

    if (this.answersFormGroup) {
      const answers = this.answersFormGroup.controls.answers as UntypedFormArray;
      for (let i = 0; i < answers.length; i += 1) {
        const fg = answers.at(i) as UntypedFormGroup;

        const answer = complementaryFormAnswers.find(
          (questionAnswer) => questionAnswer.questionId === fg.controls.id.value,
        );
        if (answer) {
          const isOpenQuestion =
            fg.controls.type.value === JobApplicationComplementaryFormQuestionTypeEnum.OpenQuestion;
          model.push(<ComplementaryFormQuestionAnswers>{
            questionLabel: answer.questionLabel,
            answerDescription: isOpenQuestion
              ? answer.answerDescription || '-'
              : fg.controls.options.value.find((f: any) => f.id === fg.controls.answerId.value)!
                  .label,
            files: answer.files ?? [],
          });
        } else {
          model.push(<ComplementaryFormQuestionAnswers>{
            questionLabel: fg.controls.label.value,
            answerDescription: '-',
            files: [],
          });
        }
      }
    }

    return model;
  }

  public getAnswers(): ComplementaryFormAnswers[] {
    const model = Array<ComplementaryFormAnswers>();
    if (this.answersFormGroup) {
      const answers = this.answersFormGroup.controls.answers as UntypedFormArray;
      for (let i = 0; i < answers.length; i += 1) {
        const fg = answers.at(i) as UntypedFormGroup;
        if (
          fg.controls.type.value === JobApplicationComplementaryFormQuestionTypeEnum.OpenQuestion &&
          (fg.controls.answerDescription.value ||
            (Array.isArray(fg.controls.files?.value) && fg.controls.files.value.length > 0))
        ) {
          const cleanedAnswer =
            PoFormUtils.sanitizeHtmlFromRichText(fg.controls.answerDescription.value) ?? '';

          model.push(<ComplementaryFormAnswers>{
            questionId: fg.controls.id.value,
            questionLabel: fg.controls.label.value,
            answerDescription: cleanedAnswer,
            questionType: fg.controls.type.value,
            files: fg.controls.files ? fg.controls.files?.value : [],
          });
        }

        if (
          fg.controls.type.value ===
            JobApplicationComplementaryFormQuestionTypeEnum.MultipleChoice &&
          fg.controls.answerId.value
        ) {
          model.push(<ComplementaryFormAnswers>{
            questionId: fg.controls.id.value,
            questionLabel: fg.controls.label.value,
            answerId: fg.controls.answerId.value,
            questionType: fg.controls.type.value,
          });
        }
      }
    }

    return model;
  }
}
